import { createSlice } from '@reduxjs/toolkit';
import { message } from 'antd';

import { apiReducerBuilder } from 'utils';
import i18n from 'utils/i18n';
import { RequestStatus } from 'utils/constants';

import {
	getTaxStatements,
	getTransactions,
	generateTaxStatement,
	downloadTaxStatement,
	downloadTransactions,
	getAccountSummary,
	getIncome,
	getInvestment,
	getDepositWithdrawal,
	getPrincipalRepayment,
	getSecondaryMarketIncome,
	getSummary,
} from './actions';

import {
	DepositWithdrawalT,
	IncomeT,
	InvestmentT,
	PrincipalRepaymentT,
	ReportsT,
	TaxStatementsT,
} from 'models';

interface Statement {
	taxStatements: TaxStatementsT;
	transactions: ReportsT;
	status: string;
	income: IncomeT;
	investment: InvestmentT;
	depositWithdrawal: DepositWithdrawalT;
	principalRepayment: PrincipalRepaymentT;
	summary: any;
}

const statementSlice = createSlice({
	name: 'settings',
	initialState: {
		taxStatements: [] as TaxStatementsT,
		transactions: [] as ReportsT,
		summary: {},
	} as Statement,
	reducers: {},
	extraReducers: (builder) => {
		apiReducerBuilder(builder, getSummary, (state, action) => {
			const { payload, meta } = action;
			state.status = meta.requestStatus;

			state.summary = { ...payload, ...payload.operations };
		});

		apiReducerBuilder(builder, getTaxStatements, (state, action) => {
			const { payload, meta } = action;
			state.status = meta.requestStatus;
			state.taxStatements = payload.data.map((item: { id: number }) => {
				return { ...item, key: item.id };
			});
		});

		apiReducerBuilder(builder, getTransactions, (state, action) => {
			const { payload, meta } = action;

			const transactions = payload.map((item: { id: number }) => {
				return { ...item, key: item.id };
			});

			state.status = meta.requestStatus;
			state.transactions =
				meta.arg.params.offset > 0 ? [...state.transactions, ...transactions] : transactions;
		});

		apiReducerBuilder(builder, downloadTaxStatement, (state, action) => {
			const { meta } = action;
			state.status = meta.requestStatus;
		});

		apiReducerBuilder(builder, generateTaxStatement, (state, action) => {
			const { meta, payload } = action;

			const { id, request } = payload;

			state.status = meta.requestStatus;
			state.taxStatements = [
				{
					id: id,
					key: id,
					timespan: `${request.startDate} - ${request.endDate}`,
					statement_status: false,
				},
				...state.taxStatements,
			];
		});

		apiReducerBuilder(builder, getAccountSummary, (state, action) => {
			const { meta, payload } = action;

			const { income, investment, depositWithdrawal, principalRepayment, summary } = payload;

			state.income = income;
			state.investment = investment;
			state.depositWithdrawal = depositWithdrawal;
			state.principalRepayment = principalRepayment;
			state.summary = { ...summary, ...summary.operations };
			state.status = meta.requestStatus;
		});

		apiReducerBuilder(builder, getIncome, (state, action) => {
			const { meta, payload } = action;

			state.income = payload;
			state.status = meta.requestStatus;
		});

		apiReducerBuilder(builder, getSecondaryMarketIncome, (state, action) => {
			const { meta, payload } = action;

			state.income = payload;
			state.status = meta.requestStatus;
		});

		apiReducerBuilder(builder, getInvestment, (state, action) => {
			const { meta, payload } = action;

			state.investment = payload;
			state.status = meta.requestStatus;
		});
		apiReducerBuilder(builder, getDepositWithdrawal, (state, action) => {
			const { meta, payload } = action;

			state.depositWithdrawal = payload;
			state.status = meta.requestStatus;
		});
		apiReducerBuilder(builder, getPrincipalRepayment, (state, action) => {
			const { meta, payload } = action;

			state.principalRepayment = payload;
			state.status = meta.requestStatus;
		});

		builder.addCase(downloadTransactions.fulfilled, (state, action) => {
			const { meta } = action;
			state.status = meta.requestStatus;
		});

		builder.addCase(downloadTransactions.rejected, (state, action) => {
			state.status = RequestStatus.FULFILLED;
			message.error(i18n.t('ImportTransactionsDateRangeError') as string);
		});

		builder.addCase(downloadTransactions.pending, (state, action) => {
			const { meta } = action;
			state.status = meta.requestStatus;
		});
	},
});

export default statementSlice.reducer;
